import { type ReactElement } from 'react';

import {
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  useController,
  type UseControllerReturn,
} from 'react-hook-form';

import useLicenseConfig from '../../hooks/useLicenseConfig';
import { type FeatureFlag } from '../../types/enums';

/**
 * Component based on `useController` hook to work with controlled component and additionally applies field configuration and feature flags.
 *
 * @example
 * ```tsx
 * function App() {
 *   const { control } = useForm<FormValues>({
 *     defaultValues: {
 *       test: ""
 *     }
 *   });
 *
 *   return (
 *     <form>
 *       <LicenseFieldController
 *         control={control}
 *         name="test"
 *         featureFlag="testEnabled"
 *         configName="item.test"
 *         render={({ field: { onChange, onBlur, value, ref }, formState, fieldState, config }) => (
 *           <>
 *             <input
 *               disabled={config.isDisabled}
 *               onChange={onChange}
 *               onBlur={onBlur}
 *               value={value}
 *               ref={ref}
 *             />
 *             <p>{formState.isSubmitted ? "submitted" : ""}</p>
 *             <p>{fieldState.isTouched ? "touched" : ""}</p>
 *           </>
 *         )}
 *       />
 *     </form>
 *   );
 * }
 * ```
 */

export type LicenseFieldProps = {
  configName?: string;
  featureFlag?: FeatureFlag;
  disabled?: boolean;
};

type LicenseFieldControllerRenderProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> = UseControllerReturn<TFieldValues, TName> & {
  config: {
    isDisabled?: boolean;
    isHidden?: boolean;
  };
};

type LicenseFieldControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<ControllerProps<TFieldValues, TName>, 'render'> &
  LicenseFieldProps & {
    render: (props: LicenseFieldControllerRenderProps<TFieldValues, TName>) => ReactElement;
  };

export const LicenseFieldController = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: LicenseFieldControllerProps<TFieldValues, TName>
) => {
  const { configName, featureFlag, ...controllerProps } = props;

  const config = useLicenseConfig({ configName, featureFlag });

  const fieldProps = useController<TFieldValues, TName>(controllerProps);

  if (config.isHidden) {
    return <></>;
  }

  return props.render({
    ...fieldProps,
    config,
  });
};
